import type { ReactNode, SVGProps } from 'react'

/* Inline SVG icons.

   The mockup this chrome came from pulled Material Symbols off the Google
   Fonts CDN. These are inline instead: an icon font is a render-blocking
   third-party request for a handful of glyphs, and this audience includes
   slow connections where the chrome would sit glyphless until it landed.

   All of them inherit `currentColor` and size from `font-size`, so a caller
   styles them like text. */

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...rest }: IconProps & { children: ReactNode }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export function CartIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="9" cy="20" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.4" fill="currentColor" stroke="none" />
      <path d="M2 3h2.2l2.4 11.4a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L20 7H5" />
    </Icon>
  )
}

export function ArrowRightIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 12h15" />
      <path d="m13 6 6 6-6 6" />
    </Icon>
  )
}

export function ArrowLeftIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M20 12H5" />
      <path d="m11 6-6 6 6 6" />
    </Icon>
  )
}

export function PhoneIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M6.5 3h3l1.5 4-2 1.3a12 12 0 0 0 5.7 5.7L16 12l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 3.5 5.2 2 2 0 0 1 5.5 3Z" />
    </Icon>
  )
}

export function TelegramIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M21 4 3 11l5 1.8L19 6.5l-8.5 8.2L10 21l3.2-4L18 20Z" />
    </Icon>
  )
}

export function ChatIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M21 11.5a8 8 0 0 1-11.7 7.1L3 20.5l1.9-6.1A8 8 0 1 1 21 11.5Z" />
    </Icon>
  )
}

export function PlayIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="4" />
      <path d="m10.5 9.5 5 2.5-5 2.5Z" fill="currentColor" />
    </Icon>
  )
}

/* ── Series card stats ───────────────────────────────────────────────────── */

export function StackIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="3" y="3.5" width="18" height="12" rx="2.5" />
      <path d="M6.5 19h11M8.5 22h7" />
    </Icon>
  )
}

export function QuestionIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.4 9.3a2.7 2.7 0 1 1 3.4 3.2c-.6.2-.9.7-.9 1.3v.4" />
      <circle cx="12" cy="17.2" r="1" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function ClockIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.2 2" />
    </Icon>
  )
}

export function TrophyIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
      <path d="M7 5.5H4.5v1A3.5 3.5 0 0 0 8 10M17 5.5h2.5v1A3.5 3.5 0 0 1 16 10" />
      <path d="M12 14v3M8.5 20h7l-.7-3h-5.6Z" />
    </Icon>
  )
}

export function TargetIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function CalendarIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </Icon>
  )
}

export function RepeatIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M4 12a8 8 0 0 1 13.7-5.6L20 8.5" />
      <path d="M20 4v4.5h-4.5" />
      <path d="M20 12a8 8 0 0 1-13.7 5.6L4 15.5" />
      <path d="M4 20v-4.5h4.5" />
    </Icon>
  )
}

export function LocationIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </Icon>
  )
}

/* ── Admin portal ────────────────────────────────────────────────────────── */

export function GridIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </Icon>
  )
}

export function UsersIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.4a6.5 6.5 0 0 1 3 5.6" />
    </Icon>
  )
}

export function PulseIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M2.5 12.5h4L9 6l3.5 12 2.5-7.5h6.5" />
    </Icon>
  )
}

export function StarIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="m12 3.6 2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 17l-5.25 2.75 1-5.85L3.5 9.75l5.9-.85Z" />
    </Icon>
  )
}

export function ShieldIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M12 3 5 5.8v5.4c0 4.2 2.9 8 7 9.8 4.1-1.8 7-5.6 7-9.8V5.8Z" />
      <path d="m9 12 2.2 2.2L15.2 10" />
    </Icon>
  )
}

export function UploadIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M12 15.5V4M8.2 7.8 12 4l3.8 3.8" />
      <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
    </Icon>
  )
}

export function ChevronRightIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="m9 5 7 7-7 7" />
    </Icon>
  )
}

export function TestPaperIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <rect x="4" y="2.5" width="16" height="19" rx="3" />
      <path d="M9.6 9.4a2.5 2.5 0 1 1 3.1 3c-.55.2-.85.65-.85 1.2v.4" />
      <circle cx="11.9" cy="17.4" r="0.95" fill="currentColor" stroke="none" />
    </Icon>
  )
}

export function FeedbackIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h8A2.5 2.5 0 0 1 16 6.5v4A2.5 2.5 0 0 1 13.5 13H8l-3.5 2.8V13a1.5 1.5 0 0 1-1.5-1.5Z" />
      <path d="M18 8.5a2.5 2.5 0 0 1 3 2.45v4.3A2.5 2.5 0 0 1 18.5 17.7v2.6L15.5 18" />
    </Icon>
  )
}

export function HistoryIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.2 8.6" />
      <path d="M3 4.5V9h4.5" />
      <path d="M12 7.6V12l3 1.8" />
    </Icon>
  )
}

export function FileUpIcon(props: IconProps): ReactNode {
  return (
    <Icon {...props}>
      <path d="M13.5 2.5H7A2.5 2.5 0 0 0 4.5 5v14A2.5 2.5 0 0 0 7 21.5h10a2.5 2.5 0 0 0 2.5-2.5V8.5Z" />
      <path d="M13.5 2.5v6h6" />
      <path d="M12 18v-5.5M9.8 14.2 12 12l2.2 2.2" />
    </Icon>
  )
}
